<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import MicroModal from 'micromodal'
import { useSearchForm, useCart } from '@/composables'
import { Common, SchoolItem } from '@/components'
import { swalError } from '@/utils'

// Router
const router = useRouter()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

// カート
const { cart } = useCart()

// トップデータ
const { searchTask, list } = useSearchForm()

function remove(sno: string, i: number) {
  cart.remove(sno)
  list.value.splice(i, 1)
}

// modelValue を監視して適宜開いたり閉じたりする
watch(
  () => props.modelValue,
  () => {
    props.modelValue ? open() : close()
  },
)

onMounted(() => {
  // 初期化時に modelValue が true ならダイアログを開く
  props.modelValue && open()
})

/** 検索ダイアログを開く */
function open() {
  MicroModal.show('cart-dialog', {
    awaitCloseAnimation: true,
    disableScroll: true,
    onClose: () => {
      emit('update:modelValue', false)
    },
  })

  if (cart.list.length > 0) {
    // カートに入っている場合は学校検索のタスクを実行
    // キャンペーンコードは空にする（キャンペーン対象外の学校が出てこなくなるため）
    searchTask.perform({ sno: cart.list.join(',') })
  }
}

/** 検索ダイアログを閉じる */
function close() {
  MicroModal.close('cart-dialog')
}
/** 学校選択に戻る */
function toTopSearch() {
  MicroModal.close('cart-dialog')
  router.push({ path: '/', hash: '#search' })
}
/** フォーム画面に遷移 */
function toForm() {
  if (cart.list.length === 0) {
    return swalError({ html: `学校が選択されていません。<br> 資料請求したい学校を選んでください` }).then(() => {
      router.push({ path: '/', hash: '#search' })
    })
  }
  MicroModal.close('cart-dialog')
  router.push({ path: '/form' })
}
</script>

<template>
  <teleport to="body">
    <div class="modal micromodal font-Murecho" id="cart-dialog" aria-hidden="true">
      <div
        class="modal__overlay fixed inset-0 bg-black/75 flex justify-center items-center z-10 will-change-transform"
        tabindex="-1"
        data-micromodal-close
      >
        <div
          class="modal__container bg-white w-full max-w-4xl h-full max-h-[80vh] rounded will-change-transform"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cart-dialog-title"
        >
          <div class="flex flex-col h-full">
            <div class="text-right p-2">
              <button
                class="text-[1.5rem] leading-none hover:opacity-80 p-2 cursor-pointer"
                aria-label="Close modal"
                data-micromodal-close
              >
                <Common.Icon name="close" />
              </button>
            </div>
            <div class="overflow-y-auto p-2 h-full">
              <template v-if="cart.list.length === 0">
                <div class="border-[#FDE0D3] border-2 rounded-md py-4 relative mt-20 w-[91%] mx-auto">
                  <p
                    class="sm:tracking-widest tracking-tight sm:leading-[2.5] leading-[2.3] sm:pl-10 pl-3 sm:text-base text-[15px]"
                  >
                    資料請求する学校がまだ選択されていません。<br />
                    学校検索から資料請求したい学校を選択し、<br class="sm:hidden block" />
                    資料請求フォームよりお申込みください。
                  </p>
                  <img
                    src="@/assets/img/note.png"
                    class="sm:h-[85px] h-[60px] absolute opacity-60 right-[18px] sm:top-[12px] top-[52px]"
                  />
                </div>
                <div class="text-center mt-12">
                  <button @click="toTopSearch" class="btn max-w-[230px] sm:max-w-[240px]">
                    <Common.Icon name="angle-left" class="mr-2" /> 学校選択に戻る
                  </button>
                </div>
              </template>

              <template v-else-if="searchTask.isRunning">
                <div class="flex items-center justify-center h-full">
                  <div class="animate-ping h-8 w-8 bg-orange rounded-full"></div>
                </div>
              </template>
              <template v-else-if="searchTask.lastSuccessful">
                <div class="flex flex-col gap-2 my-4">
                  <template v-for="(item, i) of list" :key="`school_${item.schoolNo}_${item.seikyuuCode}`">
                    <SchoolItem
                      type="removable"
                      v-bind="item"
                      @remove="remove(`${item.schoolNo}_${item.seikyuuCode}`, i)"
                    />
                  </template>
                  <div class="flex items-center sm:gap-10 gap-5 justify-center mt-8 pb-5 sm:flex-row flex-col">
                    <button @click="toTopSearch" class="btn-outline max-w-[230px] sm:max-w-[240px]">
                      <Common.Icon name="angle-left" class="mr-2" />
                      学校を選びなおす
                    </button>
                    <button @click="toForm" class="btn max-w-[230px] sm:max-w-[240px]">
                      まとめて資料請求<Common.Icon name="angle-right" class="ml-2" />
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style>
/* TODO: CSS整理 */

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(15%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideOut {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-10%);
  }
}

.micromodal {
  display: none;
}

.micromodal.is-open {
  display: block;
}

.micromodal[aria-hidden='false'] .modal__overlay {
  animation: fadeIn 0.3s cubic-bezier(0, 0, 0.2, 1);
}

.micromodal[aria-hidden='false'] .modal__container {
  animation: slideIn 0.3s cubic-bezier(0, 0, 0.2, 1);
}

.micromodal[aria-hidden='true'] .modal__overlay {
  animation: fadeOut 0.3s cubic-bezier(0, 0, 0.2, 1);
}

.micromodal[aria-hidden='true'] .modal__container {
  animation: slideOut 0.3s cubic-bezier(0, 0, 0.2, 1);
}
</style>
